import { AiAssistantBrandingModel, getBranding } from "client/Api/AiAssistant/aiAssistant";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { isNil, wrapPromiseWithCancellation } from "client/Misc/utils";
import {
	useEffect,
	useMemo,
	useState
} from "react";
import { useAiAssistantConfiguration } from "./useAiAssistantConfiguration";

export type AiAssistantBrandingResult = {
	smallAvatarUrl: string | null;
};

export function useAiAssistantBranding(): AiAssistantBrandingResult {
	const { isAiAssistantAvailable } = useAiAssistantConfiguration();
	const [branding, setBranding] = useState<AiAssistantBrandingModel | null>(null);

	useEffect(
		(): (() => void) | undefined => {
			if (!isAiAssistantAvailable) {
				return;
			}

			const cancellation: AbortController = new AbortController();

			const readBranding = async (): Promise<void> => {
				try {
					const result: AiAssistantBrandingModel | null = await wrapPromiseWithCancellation(
						readSharedBranding(),
						cancellation.signal
					);

					setBranding(result);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error("Failed to read the ai assistant branding", e);
				}
			};

			readBranding();

			return (): void => cancellation.abort();
		},
		[isAiAssistantAvailable]
	);

	return useMemo(
		(): AiAssistantBrandingResult => ({
			smallAvatarUrl: branding?.smallAvatarUrl ?? null
		}),
		[branding]
	);
}

let sharedBrandingPromise: Promise<AiAssistantBrandingModel | null> | null = null;

function readSharedBranding(): Promise<AiAssistantBrandingModel | null> {
	if (isNil(sharedBrandingPromise)) {
		sharedBrandingPromise = getBranding();

		sharedBrandingPromise.catch((): void => {
			sharedBrandingPromise = null;
		});
	}

	return sharedBrandingPromise;
}
